import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { 
  ArrowRight, 
  Shield, 
  FileText, 
  Eye, 
  Users, 
  TrendingUp,
  CheckCircle2,
  Scale,
  Lock,
  ClipboardCheck
} from 'lucide-react';

const CompliancePage = () => {
  const navigate = useNavigate();

  const supportingPoints = [
    { 
      icon: ClipboardCheck, 
      title: 'Process Rigor', 
      desc: 'Structured workflows for sponsor onboarding, project configuration, and investor subscription ensure consistency across all raises.' 
    },
    { 
      icon: FileText, 
      title: 'Project-Level Documentation', 
      desc: 'Standardized disclosure templates and documentation requirements for every project listed on the platform.' 
    },
    { 
      icon: Eye, 
      title: 'Governance Visibility', 
      desc: 'Transparent governance mechanisms allow investors to track decisions, distributions, and project updates.' 
    },
    { 
      icon: Users, 
      title: 'Investor Onboarding Standards', 
      desc: 'KYC/AML processes and eligibility verification built into the investor onboarding flow.' 
    },
    { 
      icon: TrendingUp, 
      title: 'Phased Liquidity Roadmap', 
      desc: 'A deliberate, compliance-first approach to building future liquidity pathways for tokenized assets.' 
    },
  ];

  const complianceFeatures = [
    { icon: Shield, text: 'Regulatory-aware structuring' },
    { icon: Lock, text: 'Secure data handling' },
    { icon: Scale, text: 'Jurisdiction-specific considerations' },
    { icon: FileText, text: 'Audit-ready documentation' },
  ];

  return (
    <div className="min-h-screen" data-testid="compliance-page">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-20 pb-16 md:pt-28 md:pb-24 overflow-hidden">
        {/* Background Gradient */}
        <div className="absolute inset-0 z-0">
          <div 
            className="absolute inset-0" 
            style={{ 
              background: 'linear-gradient(135deg, #090C4F 0%, #1a1f5e 50%, #D73986 100%)', 
              opacity: 0.25 
            }}
          />
        </div>

        <div className="container mx-auto px-6 md:px-12 lg:px-24 xl:px-32 relative z-10">
          <div className="max-w-4xl">
            <span className="inline-block px-4 py-1.5 rounded-full text-sm font-medium bg-primary/20 text-primary mb-6">
              Compliance & Trust
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-heading font-bold tracking-tight mb-6 leading-[1.1]" data-testid="compliance-hero-heading">
              Built for
              <br />
              <span className="text-gradient">compliant capital formation.</span>
            </h1>
            <p className="text-lg md:text-xl text-foreground-muted mb-8 max-w-2xl">
              Flippex is designed to help sponsors structure and manage tokenized real estate offerings with clearer workflows, stronger disclosure standards, and governance designed into the platform experience.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                size="lg"
                className="bg-primary hover:bg-primary-hover rounded-full px-8"
                onClick={() => navigate('/for-developers')}
                data-testid="compliance-learn-more-btn"
              >
                Learn More <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="rounded-full px-8"
                onClick={() => navigate('/contact')}
              >
                Contact Us
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Compliance Features Strip */}
      <section className="py-12 bg-background-paper/50 border-y border-white/5">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 xl:px-32">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            {complianceFeatures.map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <div key={idx} className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Icon className="h-5 w-5 text-primary" />
                  </div>
                  <span className="text-sm md:text-base font-medium">{feature.text}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Supporting Points */}
      <section className="py-16 md:py-20">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 xl:px-32">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
              How We Support Compliance
            </h2>
            <p className="text-lg text-foreground-muted max-w-2xl mx-auto">
              Our platform is built with compliance considerations at every step.
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-6">
            {supportingPoints.map((point, idx) => {
              const Icon = point.icon;
              return (
                <div 
                  key={idx} 
                  className="p-6 rounded-2xl bg-background-paper border border-white/5 hover:border-primary/30 transition-colors flex gap-6 items-start"
                >
                  <div className="h-14 w-14 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Icon className="h-7 w-7 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">{point.title}</h3>
                    <p className="text-foreground-muted">{point.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Disclaimer Section */}
      <section className="py-12 bg-background-paper/50">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 xl:px-32">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-sm text-foreground-muted">
              <strong>Important Notice:</strong> Flippex provides infrastructure and tools for compliant capital formation. 
              Each project listed on the platform is structured and managed by its respective sponsor. 
              Investors should conduct their own due diligence and consult with qualified legal and financial advisors 
              before making any investment decisions. Past performance is not indicative of future results.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-primary/10 via-background to-accent/10">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 xl:px-32">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
              Ready to Learn More?
            </h2>
            <p className="text-lg text-foreground-muted mb-8">
              Explore how Flippex can support your capital-raising needs with compliant infrastructure.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-primary hover:bg-primary-hover rounded-full px-8"
                onClick={() => navigate('/for-developers')}
              >
                For Developers <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="rounded-full px-8"
                onClick={() => navigate('/for-investors')}
              >
                For Investors
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CompliancePage;
